import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
    FaTachometerAlt, FaBox, FaShoppingBag, FaUsers, FaEnvelope, FaGraduationCap,
    FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaChartLine, FaImages, FaTruck,
    FaCheck, FaBan, FaStar, FaComments, FaEye, FaEyeSlash, FaWarehouse
} from 'react-icons/fa';
import {
    getAdminStats, getProducts, getAdminOrders, updateOrderStatus,
    getAdminUsers, createAdminUser, getAdminMessages, deleteAdminMessage,
    createProduct, updateProduct, deleteProduct, bulkDeleteProducts,
    getProductImages, addProductImage, deleteProductImage,
    getCourses, createCourse, updateCourse, deleteCourse, bulkDeleteCourses,
    getAdminProviderGoods, approveProviderGood, rejectProviderGood,
    getAdminReviews, setReviewVisibility, deleteAdminReview,
    getInventoryOverview, getDailyInventory, getMonthlyInventory, adjustStock,
    getCompleteInventory, uploadFile
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import Loader from '../components/Loader';
import InventoryDashboard from '../components/InventoryDashboard';
import './Admin.css';

const T = {
    en: {
        // Sidebar / tabs
        adminPanel: 'Admin Panel',
        tabDashboard: 'Dashboard',
        tabProducts: 'Products',
        tabInventory: 'Stock Inventory',
        tabCourses: 'Courses',
        tabOrders: 'Orders',
        tabProviderGoods: 'Provider Goods',
        tabReviews: 'Reviews',
        tabUsers: 'Users',
        tabMessages: 'Messages',
        // Permission options
        permProducts: 'Products',
        permInventory: 'Stock Inventory',
        permCourses: 'Courses',
        permOrders: 'Orders',
        permProviderGoods: 'Provider Goods',
        permReviews: 'Reviews',
        permMessages: 'Messages',
        // Loaders
        loadingDashboard: 'Loading dashboard',
        loadingProducts: 'Loading products',
        loadingCourses: 'Loading courses',
        loadingProviderGoods: 'Loading provider goods',
        loadingReviews: 'Loading reviews',
        loadingOrders: 'Loading orders',
        loadingUsers: 'Loading users',
        loadingMessages: 'Loading messages',
        uploading: 'Uploading',
        // Dashboard
        dashboard: 'Dashboard',
        statProducts: 'Products',
        statCourses: 'Courses',
        statOrders: 'Orders',
        statUsers: 'Users',
        totalRevenue: 'Total Revenue',
        pendingProviderGoods: 'Pending Provider Goods',
        recentOrders: 'Recent Orders',
        colId: 'ID',
        colCustomer: 'Customer',
        colAmount: 'Amount',
        colStatus: 'Status',
        colDate: 'Date',
        // Products
        productsCount: (n) => `Products (${n})`,
        deleteSelected: (n) => `Delete Selected (${n})`,
        addProduct: 'Add Product',
        colImage: 'Image',
        colName: 'Name',
        colCategory: 'Category',
        colPrice: 'Price',
        colStock: 'Stock',
        colCbm: 'CBM',
        colCbmPrice: 'CBM Price',
        colActions: 'Actions',
        noImg: 'No img',
        manageImages: 'Manage images',
        editProduct: 'Edit Product',
        addNewProduct: 'Add New Product',
        productNameLabel: 'Product Name *',
        categoryLabel: 'Category *',
        descriptionLabel: 'Description',
        priceXafLabel: 'Price (XAF) *',
        stockQuantityLabel: 'Stock Quantity',
        cbmLabel: 'CBM (m³) - L x W x H',
        cbmPlaceholder: 'e.g. 0.0360',
        cbmPriceLabel: 'CBM Price (XAF per m³)',
        cbmPricePlaceholder: 'e.g. 150000',
        mainImageLabel: 'Main Image',
        pasteUrlProduct: '...or paste a URL: https://... or /images/product.jpg',
        previewAlt: 'Preview',
        updateProduct: 'Update Product',
        createProduct: 'Create Product',
        // Image manager
        imagesFor: (name) => `Images - ${name}`,
        noVariantImages: 'No variant images yet. Add some below.',
        noLabel: 'No label',
        addNewImage: 'Add New Image',
        imageLabel: 'Image *',
        pasteUrlVariant: '...or paste a URL: https://... or /images/variant.jpg',
        labelHint: 'Label (e.g. "Black", "Size L")',
        variantNamePlaceholder: 'Variant name',
        addImage: 'Add Image',
        // Courses
        coursesCount: (n) => `Courses (${n})`,
        addCourse: 'Add Course',
        colTitle: 'Title',
        colType: 'Type',
        colDuration: 'Duration',
        noCoursesYet: 'No courses yet. Add one!',
        editCourse: 'Edit Course',
        addNewCourse: 'Add New Course',
        courseTitleLabel: 'Course Title *',
        courseTypeLabel: 'Course Type *',
        optOnline: 'Online',
        optOnsite: 'Onsite',
        optVideo: 'Video',
        priceXafLabelPlain: 'Price (XAF)',
        durationLabel: 'Duration',
        durationPlaceholder: 'e.g. 4 weeks',
        imageUrlLabel: 'Image URL',
        urlPlaceholder: 'https://...',
        updateCourse: 'Update Course',
        createCourse: 'Create Course',
        // Provider goods
        providerGoodsCount: (n) => `Provider Goods (${n})`,
        pendingStat: (n) => `${n} Pending`,
        approvedStat: (n) => `${n} Approved`,
        rejectedStat: (n) => `${n} Rejected`,
        colQty: 'Qty',
        colProvider: 'Provider',
        noteOptional: 'Note (optional)',
        approveAddToStore: 'Approve & add to store',
        reject: 'Reject',
        noProviderGoods: 'No provider goods submitted yet.',
        // Reviews
        customerReviewsCount: (n) => `Customer Reviews (${n})`,
        visibleStat: (n) => `${n} Visible`,
        hiddenStat: (n) => `${n} Hidden`,
        colProduct: 'Product',
        colRating: 'Rating',
        colComment: 'Comment',
        noComment: 'No comment',
        hidden: 'Hidden',
        visible: 'Visible',
        showReview: 'Show review',
        hideReview: 'Hide review',
        deleteReview: 'Delete review',
        noReviews: 'No reviews yet.',
        // Orders
        ordersCount: (n) => `Orders (${n})`,
        colEmail: 'Email',
        statusPending: 'Pending',
        statusProcessing: 'Processing',
        statusShipped: 'Shipped',
        statusDelivered: 'Delivered',
        statusCancelled: 'Cancelled',
        noOrders: 'No orders yet.',
        // Users
        usersCount: (n) => `Users (${n})`,
        addUser: 'Add User',
        colRole: 'Role',
        colPermissions: 'Permissions',
        colJoined: 'Joined',
        fullAccess: 'Full access',
        createNewUser: 'Create New User',
        fullNameLabel: 'Full Name *',
        emailLabel: 'Email *',
        passwordLabel: 'Password *',
        passwordPlaceholder: 'Min 6 characters',
        roleLabel: 'Role',
        roleUser: 'User',
        roleAdmin: 'Admin',
        roleProvider: 'Provider',
        adminPanelAccess: 'Admin Panel Access (Permissions)',
        permHelp: 'Tick the sections this user should be able to manage. Admins always have full access.',
        emailNote: 'An email with the credentials will be sent to the user automatically (if SMTP is configured).',
        createUserSendEmail: 'Create User & Send Email',
        // Messages
        contactMessagesCount: (n) => `Contact Messages (${n})`,
        phoneLabel: (phone) => `Phone: ${phone}`,
        noMessages: 'No messages yet.',
        // Alerts / confirms
        uploadFailed: 'Upload failed',
        operationFailed: 'Operation failed',
        confirmDeleteProduct: 'Are you sure you want to delete this product?',
        failedDeleteProduct: 'Failed to delete product',
        confirmBulkDeleteProducts: (n) => `Delete ${n} selected product(s)? This cannot be undone.`,
        failedBulkDeleteProducts: 'Failed to bulk delete products',
        failedAddImage: 'Failed to add image',
        failedDeleteImage: 'Failed to delete image',
        confirmDeleteCourse: 'Are you sure you want to delete this course?',
        failedDeleteCourse: 'Failed to delete course',
        confirmBulkDeleteCourses: (n) => `Delete ${n} selected course(s)? This cannot be undone.`,
        failedBulkDeleteCourses: 'Failed to bulk delete courses',
        failedUpdateStatus: 'Failed to update status',
        failedDeleteMessage: 'Failed to delete message',
        confirmApproveGood: 'Approve this good and add it to the store?',
        failedApproveGood: 'Failed to approve good',
        confirmRejectGood: 'Reject this good?',
        failedRejectGood: 'Failed to reject good',
        failedUpdateVisibility: 'Failed to update visibility',
        confirmDeleteReview: 'Delete this review? This cannot be undone.',
        failedDeleteReview: 'Failed to delete review',
        userCreatedEmailed: (email) => `User created. Credentials emailed to ${email}.`,
        userCreatedNotEmailed: (status) => `User created, but the email was NOT sent (${status}). Share the password manually.`,
        failedCreateUser: 'Failed to create user',
    },
    fr: {
        // Sidebar / tabs
        adminPanel: 'Panneau d\'administration',
        tabDashboard: 'Tableau de bord',
        tabProducts: 'Produits',
        tabInventory: 'Inventaire des stocks',
        tabCourses: 'Formations',
        tabOrders: 'Commandes',
        tabProviderGoods: 'Marchandises des fournisseurs',
        tabReviews: 'Avis',
        tabUsers: 'Utilisateurs',
        tabMessages: 'Messages',
        // Permission options
        permProducts: 'Produits',
        permInventory: 'Inventaire des stocks',
        permCourses: 'Formations',
        permOrders: 'Commandes',
        permProviderGoods: 'Marchandises des fournisseurs',
        permReviews: 'Avis',
        permMessages: 'Messages',
        // Loaders
        loadingDashboard: 'Chargement du tableau de bord',
        loadingProducts: 'Chargement des produits',
        loadingCourses: 'Chargement des formations',
        loadingProviderGoods: 'Chargement des marchandises des fournisseurs',
        loadingReviews: 'Chargement des avis',
        loadingOrders: 'Chargement des commandes',
        loadingUsers: 'Chargement des utilisateurs',
        loadingMessages: 'Chargement des messages',
        uploading: 'Téléversement',
        // Dashboard
        dashboard: 'Tableau de bord',
        statProducts: 'Produits',
        statCourses: 'Formations',
        statOrders: 'Commandes',
        statUsers: 'Utilisateurs',
        totalRevenue: 'Chiffre d\'affaires total',
        pendingProviderGoods: 'Marchandises de fournisseurs en attente',
        recentOrders: 'Commandes récentes',
        colId: 'ID',
        colCustomer: 'Client',
        colAmount: 'Montant',
        colStatus: 'Statut',
        colDate: 'Date',
        // Products
        productsCount: (n) => `Produits (${n})`,
        deleteSelected: (n) => `Supprimer la sélection (${n})`,
        addProduct: 'Ajouter un produit',
        colImage: 'Image',
        colName: 'Nom',
        colCategory: 'Catégorie',
        colPrice: 'Prix',
        colStock: 'Stock',
        colCbm: 'CBM',
        colCbmPrice: 'Prix CBM',
        colActions: 'Actions',
        noImg: 'Aucune image',
        manageImages: 'Gérer les images',
        editProduct: 'Modifier le produit',
        addNewProduct: 'Ajouter un nouveau produit',
        productNameLabel: 'Nom du produit *',
        categoryLabel: 'Catégorie *',
        descriptionLabel: 'Description',
        priceXafLabel: 'Prix (XAF) *',
        stockQuantityLabel: 'Quantité en stock',
        cbmLabel: 'CBM (m³) - L x l x H',
        cbmPlaceholder: 'ex. 0,0360',
        cbmPriceLabel: 'Prix CBM (XAF par m³)',
        cbmPricePlaceholder: 'ex. 150000',
        mainImageLabel: 'Image principale',
        pasteUrlProduct: '...ou collez une URL : https://... ou /images/product.jpg',
        previewAlt: 'Aperçu',
        updateProduct: 'Mettre à jour le produit',
        createProduct: 'Créer le produit',
        // Image manager
        imagesFor: (name) => `Images - ${name}`,
        noVariantImages: 'Aucune image de variante pour le moment. Ajoutez-en ci-dessous.',
        noLabel: 'Aucun libellé',
        addNewImage: 'Ajouter une nouvelle image',
        imageLabel: 'Image *',
        pasteUrlVariant: '...ou collez une URL : https://... ou /images/variant.jpg',
        labelHint: 'Libellé (ex. « Noir », « Taille L »)',
        variantNamePlaceholder: 'Nom de la variante',
        addImage: 'Ajouter une image',
        // Courses
        coursesCount: (n) => `Formations (${n})`,
        addCourse: 'Ajouter une formation',
        colTitle: 'Titre',
        colType: 'Type',
        colDuration: 'Durée',
        noCoursesYet: 'Aucune formation pour le moment. Ajoutez-en une !',
        editCourse: 'Modifier la formation',
        addNewCourse: 'Ajouter une nouvelle formation',
        courseTitleLabel: 'Titre de la formation *',
        courseTypeLabel: 'Type de formation *',
        optOnline: 'En ligne',
        optOnsite: 'En présentiel',
        optVideo: 'Vidéo',
        priceXafLabelPlain: 'Prix (XAF)',
        durationLabel: 'Durée',
        durationPlaceholder: 'ex. 4 semaines',
        imageUrlLabel: 'URL de l\'image',
        urlPlaceholder: 'https://...',
        updateCourse: 'Mettre à jour la formation',
        createCourse: 'Créer la formation',
        // Provider goods
        providerGoodsCount: (n) => `Marchandises des fournisseurs (${n})`,
        pendingStat: (n) => `${n} en attente`,
        approvedStat: (n) => `${n} approuvées`,
        rejectedStat: (n) => `${n} rejetées`,
        colQty: 'Qté',
        colProvider: 'Fournisseur',
        noteOptional: 'Note (facultative)',
        approveAddToStore: 'Approuver et ajouter à la boutique',
        reject: 'Rejeter',
        noProviderGoods: 'Aucune marchandise de fournisseur soumise pour le moment.',
        // Reviews
        customerReviewsCount: (n) => `Avis des clients (${n})`,
        visibleStat: (n) => `${n} visibles`,
        hiddenStat: (n) => `${n} masqués`,
        colProduct: 'Produit',
        colRating: 'Note',
        colComment: 'Commentaire',
        noComment: 'Aucun commentaire',
        hidden: 'Masqué',
        visible: 'Visible',
        showReview: 'Afficher l\'avis',
        hideReview: 'Masquer l\'avis',
        deleteReview: 'Supprimer l\'avis',
        noReviews: 'Aucun avis pour le moment.',
        // Orders
        ordersCount: (n) => `Commandes (${n})`,
        colEmail: 'E-mail',
        statusPending: 'En attente',
        statusProcessing: 'En traitement',
        statusShipped: 'Expédiée',
        statusDelivered: 'Livrée',
        statusCancelled: 'Annulée',
        noOrders: 'Aucune commande pour le moment.',
        // Users
        usersCount: (n) => `Utilisateurs (${n})`,
        addUser: 'Ajouter un utilisateur',
        colRole: 'Rôle',
        colPermissions: 'Autorisations',
        colJoined: 'Inscription',
        fullAccess: 'Accès complet',
        createNewUser: 'Créer un nouvel utilisateur',
        fullNameLabel: 'Nom complet *',
        emailLabel: 'E-mail *',
        passwordLabel: 'Mot de passe *',
        passwordPlaceholder: 'Au moins 6 caractères',
        roleLabel: 'Rôle',
        roleUser: 'Utilisateur',
        roleAdmin: 'Administrateur',
        roleProvider: 'Fournisseur',
        adminPanelAccess: 'Accès au panneau d\'administration (autorisations)',
        permHelp: 'Cochez les sections que cet utilisateur doit pouvoir gérer. Les administrateurs disposent toujours d\'un accès complet.',
        emailNote: 'Un e-mail contenant les identifiants sera envoyé automatiquement à l\'utilisateur (si le SMTP est configuré).',
        createUserSendEmail: 'Créer l\'utilisateur et envoyer l\'e-mail',
        // Messages
        contactMessagesCount: (n) => `Messages de contact (${n})`,
        phoneLabel: (phone) => `Téléphone : ${phone}`,
        noMessages: 'Aucun message pour le moment.',
        // Alerts / confirms
        uploadFailed: 'Échec du téléversement',
        operationFailed: 'Échec de l\'opération',
        confirmDeleteProduct: 'Êtes-vous sûr de vouloir supprimer ce produit ?',
        failedDeleteProduct: 'Échec de la suppression du produit',
        confirmBulkDeleteProducts: (n) => `Supprimer ${n} produit(s) sélectionné(s) ? Cette action est irréversible.`,
        failedBulkDeleteProducts: 'Échec de la suppression groupée des produits',
        failedAddImage: 'Échec de l\'ajout de l\'image',
        failedDeleteImage: 'Échec de la suppression de l\'image',
        confirmDeleteCourse: 'Êtes-vous sûr de vouloir supprimer cette formation ?',
        failedDeleteCourse: 'Échec de la suppression de la formation',
        confirmBulkDeleteCourses: (n) => `Supprimer ${n} formation(s) sélectionnée(s) ? Cette action est irréversible.`,
        failedBulkDeleteCourses: 'Échec de la suppression groupée des formations',
        failedUpdateStatus: 'Échec de la mise à jour du statut',
        failedDeleteMessage: 'Échec de la suppression du message',
        confirmApproveGood: 'Approuver cette marchandise et l\'ajouter à la boutique ?',
        failedApproveGood: 'Échec de l\'approbation de la marchandise',
        confirmRejectGood: 'Rejeter cette marchandise ?',
        failedRejectGood: 'Échec du rejet de la marchandise',
        failedUpdateVisibility: 'Échec de la mise à jour de la visibilité',
        confirmDeleteReview: 'Supprimer cet avis ? Cette action est irréversible.',
        failedDeleteReview: 'Échec de la suppression de l\'avis',
        userCreatedEmailed: (email) => `Utilisateur créé. Identifiants envoyés par e-mail à ${email}.`,
        userCreatedNotEmailed: (status) => `Utilisateur créé, mais l'e-mail n'a PAS été envoyé (${status}). Communiquez le mot de passe manuellement.`,
        failedCreateUser: 'Échec de la création de l\'utilisateur',
    },
};

const adminInventoryApi = {
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getInventoryOverview,
    getDailyInventory,
    getMonthlyInventory,
    adjustStock,
    getCompleteInventory,
};

function Admin() {
    const { user } = useAuth();
    const t = useT(T);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [stats, setStats] = useState(null);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [users, setUsers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [courses, setCourses] = useState([]);
    const [providerGoods, setProviderGoods] = useState([]);
    const [reviews, setReviews] = useState([]);

    // Provider goods
    const [adminNoteInput, setAdminNoteInput] = useState('');

    // Product form
    const [showForm, setShowForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '', description: '', price: '', category: '', image_url: '', stock_quantity: '', cbm: '', cbm_price: ''
    });

    // Image upload state
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingVariant, setUploadingVariant] = useState(false);

    // Product images
    const [showImageManager, setShowImageManager] = useState(null);
    const [productImgs, setProductImgs] = useState([]);
    const [newImage, setNewImage] = useState({ image_url: '', label: '' });

    // Bulk selection
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [selectedCourseIds, setSelectedCourseIds] = useState([]);

    // Course form
    const [showCourseForm, setShowCourseForm] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null);
    const [courseFormData, setCourseFormData] = useState({
        title: '', description: '', course_type: 'online', price: '', duration: '', image_url: ''
    });

    // User form
    const [showUserForm, setShowUserForm] = useState(false);
    const [userFormData, setUserFormData] = useState({ name: '', email: '', password: '', role: 'user', permissions: [] });
    const PERMISSION_OPTIONS = [
        { key: 'products', label: t.permProducts },
        { key: 'inventory', label: t.permInventory },
        { key: 'courses', label: t.permCourses },
        { key: 'orders', label: t.permOrders },
        { key: 'provider-goods', label: t.permProviderGoods },
        { key: 'reviews', label: t.permReviews },
        { key: 'messages', label: t.permMessages },
    ];

    const [tabLoading, setTabLoading] = useState({});

    const withLoading = (key, promise) => {
        setTabLoading(s => ({ ...s, [key]: true }));
        return promise.finally(() => setTabLoading(s => ({ ...s, [key]: false })));
    };

    useEffect(() => {
        if (user?.role === 'admin') {
            if (activeTab === 'dashboard') loadStats();
            if (activeTab === 'products') loadProducts();
            if (activeTab === 'orders') loadOrders();
            if (activeTab === 'users') loadUsers();
            if (activeTab === 'messages') loadMessages();
            if (activeTab === 'courses') loadCourses();
            if (activeTab === 'provider-goods') loadProviderGoods();
            if (activeTab === 'reviews') loadReviews();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, user]);

    const userPerms = Array.isArray(user?.permissions) ? user.permissions : [];
    const isAdmin = user?.role === 'admin';
    const canAccessAdmin = isAdmin || userPerms.length > 0;
    const canSee = (section) => isAdmin || userPerms.includes(section);

    if (!user || !canAccessAdmin) return <Navigate to="/" />;

    const loadStats = () => withLoading('dashboard', getAdminStats().then(r => setStats(r.data)).catch(() => {}));
    const loadProducts = () => withLoading('products', getProducts().then(r => setProducts(r.data)).catch(() => {}));
    const loadOrders = () => withLoading('orders', getAdminOrders().then(r => setOrders(r.data)).catch(() => {}));
    const loadUsers = () => withLoading('users', getAdminUsers().then(r => setUsers(r.data)).catch(() => {}));
    const loadMessages = () => withLoading('messages', getAdminMessages().then(r => setMessages(r.data)).catch(() => {}));
    const loadCourses = () => withLoading('courses', getCourses().then(r => setCourses(r.data)).catch(() => {}));
    const loadProviderGoods = () => withLoading('provider-goods', getAdminProviderGoods().then(r => setProviderGoods(r.data)).catch(() => {}));
    const loadReviews = () => withLoading('reviews', getAdminReviews().then(r => setReviews(r.data)).catch(() => {}));

    // Upload a file from the device, then apply the resulting URL via `apply`.
    const handleFileUpload = async (file, apply, setBusy) => {
        if (!file) return;
        setBusy(true);
        try {
            const url = await uploadFile(file);
            apply(url);
        } catch (err) {
            alert(err.response?.data?.error || t.uploadFailed);
        } finally {
            setBusy(false);
        }
    };

    // ===== PRODUCTS =====
    const openCreateForm = () => {
        setEditingProduct(null);
        setFormData({ name: '', description: '', price: '', category: '', image_url: '', stock_quantity: '', cbm: '', cbm_price: '' });
        setShowForm(true);
    };

    const openEditForm = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name, description: product.description || '',
            price: product.price, category: product.category,
            image_url: product.image_url || '', stock_quantity: product.stock_quantity,
            cbm: product.cbm || '', cbm_price: product.cbm_price || ''
        });
        setShowForm(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = { ...formData, price: Number(formData.price), stock_quantity: Number(formData.stock_quantity), cbm: formData.cbm ? Number(formData.cbm) : null, cbm_price: formData.cbm_price ? Number(formData.cbm_price) : null };
            if (editingProduct) {
                await updateProduct(editingProduct.id, data);
            } else {
                await createProduct(data);
            }
            setShowForm(false);
            loadProducts();
        } catch (err) {
            alert(err.response?.data?.error || t.operationFailed);
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm(t.confirmDeleteProduct)) return;
        try {
            await deleteProduct(id);
            loadProducts();
        } catch { alert(t.failedDeleteProduct); }
    };

    const toggleProductSelection = (id) => {
        setSelectedProductIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAllProducts = () => {
        if (selectedProductIds.length === products.length) setSelectedProductIds([]);
        else setSelectedProductIds(products.map(p => p.id));
    };

    const handleBulkDeleteProducts = async () => {
        if (selectedProductIds.length === 0) return;
        if (!window.confirm(t.confirmBulkDeleteProducts(selectedProductIds.length))) return;
        try {
            await bulkDeleteProducts(selectedProductIds);
            setSelectedProductIds([]);
            loadProducts();
        } catch (err) {
            alert(err.response?.data?.error || t.failedBulkDeleteProducts);
        }
    };

    // ===== PRODUCT IMAGES =====
    const openImageManager = async (product) => {
        setShowImageManager(product);
        setNewImage({ image_url: '', label: '' });
        try {
            const res = await getProductImages(product.id);
            setProductImgs(res.data);
        } catch { setProductImgs([]); }
    };

    const handleAddImage = async (e) => {
        e.preventDefault();
        if (!newImage.image_url) return;
        try {
            const res = await addProductImage(showImageManager.id, newImage);
            setProductImgs(res.data);
            setNewImage({ image_url: '', label: '' });
        } catch { alert(t.failedAddImage); }
    };

    const handleDeleteImage = async (imgId) => {
        try {
            await deleteProductImage(imgId);
            setProductImgs(prev => prev.filter(i => i.id !== imgId));
        } catch { alert(t.failedDeleteImage); }
    };

    // ===== COURSES =====
    const openCreateCourse = () => {
        setEditingCourse(null);
        setCourseFormData({ title: '', description: '', course_type: 'online', price: '', duration: '', image_url: '' });
        setShowCourseForm(true);
    };

    const openEditCourse = (course) => {
        setEditingCourse(course);
        setCourseFormData({
            title: course.title, description: course.description || '',
            course_type: course.course_type, price: course.price,
            duration: course.duration || '', image_url: course.image_url || ''
        });
        setShowCourseForm(true);
    };

    const handleCourseSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = { ...courseFormData, price: Number(courseFormData.price) };
            if (editingCourse) {
                await updateCourse(editingCourse.id, data);
            } else {
                await createCourse(data);
            }
            setShowCourseForm(false);
            loadCourses();
        } catch (err) {
            alert(err.response?.data?.error || t.operationFailed);
        }
    };

    const handleDeleteCourse = async (id) => {
        if (!window.confirm(t.confirmDeleteCourse)) return;
        try { await deleteCourse(id); loadCourses(); } catch { alert(t.failedDeleteCourse); }
    };

    const toggleCourseSelection = (id) => {
        setSelectedCourseIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAllCourses = () => {
        if (selectedCourseIds.length === courses.length) setSelectedCourseIds([]);
        else setSelectedCourseIds(courses.map(c => c.id));
    };

    const handleBulkDeleteCourses = async () => {
        if (selectedCourseIds.length === 0) return;
        if (!window.confirm(t.confirmBulkDeleteCourses(selectedCourseIds.length))) return;
        try {
            await bulkDeleteCourses(selectedCourseIds);
            setSelectedCourseIds([]);
            loadCourses();
        } catch (err) {
            alert(err.response?.data?.error || t.failedBulkDeleteCourses);
        }
    };

    // ===== ORDERS =====
    const handleStatusChange = async (orderId, status) => {
        try { await updateOrderStatus(orderId, status); loadOrders(); } catch { alert(t.failedUpdateStatus); }
    };

    const handleDeleteMessage = async (id) => {
        try { await deleteAdminMessage(id); loadMessages(); } catch { alert(t.failedDeleteMessage); }
    };

    // ===== PROVIDER GOODS =====
    const handleApproveGood = async (id) => {
        if (!window.confirm(t.confirmApproveGood)) return;
        try {
            await approveProviderGood(id, { admin_note: adminNoteInput });
            setAdminNoteInput('');
            loadProviderGoods();
        } catch { alert(t.failedApproveGood); }
    };

    const handleRejectGood = async (id) => {
        if (!window.confirm(t.confirmRejectGood)) return;
        try {
            await rejectProviderGood(id, { admin_note: adminNoteInput });
            setAdminNoteInput('');
            loadProviderGoods();
        } catch { alert(t.failedRejectGood); }
    };

    // ===== REVIEWS =====
    const handleToggleReviewVisibility = async (review) => {
        try {
            await setReviewVisibility(review.id, !review.is_hidden);
            loadReviews();
        } catch { alert(t.failedUpdateVisibility); }
    };

    const handleDeleteReview = async (id) => {
        if (!window.confirm(t.confirmDeleteReview)) return;
        try { await deleteAdminReview(id); loadReviews(); } catch { alert(t.failedDeleteReview); }
    };

    const renderStars = (rating) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(<FaStar key={i} className={`review-star ${i <= rating ? 'filled' : ''}`} />);
        }
        return stars;
    };

    // ===== USERS =====
    const togglePerm = (key) => {
        setUserFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(key)
                ? prev.permissions.filter(p => p !== key)
                : [...prev.permissions, key]
        }));
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const res = await createAdminUser(userFormData);
            setShowUserForm(false);
            setUserFormData({ name: '', email: '', password: '', role: 'user', permissions: [] });
            loadUsers();
            const emailSent = res?.data?.email_sent;
            const emailStatus = res?.data?.email_status;
            if (emailSent) {
                alert(t.userCreatedEmailed(res.data.email));
            } else {
                alert(t.userCreatedNotEmailed(emailStatus));
            }
        } catch (err) {
            alert(err.response?.data?.error || t.failedCreateUser);
        }
    };

    const allTabs = [
        { key: 'dashboard', label: t.tabDashboard, icon: <FaTachometerAlt />, section: null },
        { key: 'products', label: t.tabProducts, icon: <FaBox />, section: 'products' },
        { key: 'inventory', label: t.tabInventory, icon: <FaWarehouse />, section: 'inventory' },
        { key: 'courses', label: t.tabCourses, icon: <FaGraduationCap />, section: 'courses' },
        { key: 'orders', label: t.tabOrders, icon: <FaShoppingBag />, section: 'orders' },
        { key: 'provider-goods', label: t.tabProviderGoods, icon: <FaTruck />, section: 'provider-goods' },
        { key: 'reviews', label: t.tabReviews, icon: <FaComments />, section: 'reviews' },
        { key: 'users', label: t.tabUsers, icon: <FaUsers />, section: 'admin-only' },
        { key: 'messages', label: t.tabMessages, icon: <FaEnvelope />, section: 'messages' },
    ];
    const tabs = allTabs.filter(t => t.section === null || (t.section === 'admin-only' ? isAdmin : canSee(t.section)));

    return (
        <div className="admin-page">
            <div className="admin-sidebar">
                <h2 className="admin-logo">{t.adminPanel}</h2>
                <nav className="admin-nav">
                    {tabs.map(tab => (
                        <button key={tab.key} className={`admin-nav-btn ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}>
                            {tab.icon} <span>{tab.label}</span>
                        </button>
                    ))}
                </nav>
            </div>

            <div className="admin-content">

                {/* ===== DASHBOARD ===== */}
                {activeTab === 'dashboard' && !stats && tabLoading.dashboard && (
                    <Loader label={t.loadingDashboard} />
                )}
                {activeTab === 'dashboard' && stats && (
                    <div className="admin-dashboard">
                        <h1>{t.dashboard}</h1>
                        <div className="stats-grid">
                            <div className="stat-card"><FaBox className="stat-icon" /><div><h3>{stats.totalProducts}</h3><p>{t.statProducts}</p></div></div>
                            <div className="stat-card"><FaGraduationCap className="stat-icon" /><div><h3>{stats.totalCourses}</h3><p>{t.statCourses}</p></div></div>
                            <div className="stat-card"><FaShoppingBag className="stat-icon" /><div><h3>{stats.totalOrders}</h3><p>{t.statOrders}</p></div></div>
                            <div className="stat-card"><FaUsers className="stat-icon" /><div><h3>{stats.totalUsers}</h3><p>{t.statUsers}</p></div></div>
                            <div className="stat-card revenue"><FaChartLine className="stat-icon" /><div><h3>{Number(stats.totalRevenue).toLocaleString()} XAF</h3><p>{t.totalRevenue}</p></div></div>
                            {stats.pendingProviderGoods > 0 && (
                                <div className="stat-card pending-provider"><FaTruck className="stat-icon" /><div><h3>{stats.pendingProviderGoods}</h3><p>{t.pendingProviderGoods}</p></div></div>
                            )}
                        </div>
                        {stats.recentOrders.length > 0 && (
                            <div className="admin-section">
                                <h2>{t.recentOrders}</h2>
                                <table className="admin-table">
                                    <thead><tr><th>{t.colId}</th><th>{t.colCustomer}</th><th>{t.colAmount}</th><th>{t.colStatus}</th><th>{t.colDate}</th></tr></thead>
                                    <tbody>
                                        {stats.recentOrders.map(order => (
                                            <tr key={order.id}>
                                                <td>#{order.id}</td><td>{order.customer_name}</td>
                                                <td>{Number(order.total_amount).toLocaleString()} XAF</td>
                                                <td><span className={`status-badge ${order.status}`}>{order.status}</span></td>
                                                <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ===== PRODUCTS ===== */}
                {activeTab === 'products' && (
                    <div className="admin-products">
                        <div className="admin-header">
                            <h1>{t.productsCount(products.length)}</h1>
                            <div className="admin-header-actions">
                                {selectedProductIds.length > 0 && (
                                    <button className="admin-btn danger" onClick={handleBulkDeleteProducts}>
                                        <FaTrash /> {t.deleteSelected(selectedProductIds.length)}
                                    </button>
                                )}
                                <button className="admin-btn primary" onClick={openCreateForm}><FaPlus /> {t.addProduct}</button>
                            </div>
                        </div>
                        {tabLoading.products && products.length === 0 && <Loader label={t.loadingProducts} />}
                        <table className="admin-table">
                            <thead><tr>
                                <th className="select-col"><input type="checkbox" checked={products.length > 0 && selectedProductIds.length === products.length} onChange={toggleAllProducts} /></th>
                                <th>{t.colId}</th><th>{t.colImage}</th><th>{t.colName}</th><th>{t.colCategory}</th><th>{t.colPrice}</th><th>{t.colStock}</th><th>{t.colCbm}</th><th>{t.colCbmPrice}</th><th>{t.colActions}</th>
                            </tr></thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.id} className={selectedProductIds.includes(p.id) ? 'row-selected' : ''}>
                                        <td className="select-col"><input type="checkbox" checked={selectedProductIds.includes(p.id)} onChange={() => toggleProductSelection(p.id)} /></td>
                                        <td>#{p.id}</td>
                                        <td>
                                            {p.image_url
                                                ? <img src={p.image_url} alt={p.name} className="table-thumb" />
                                                : <span className="no-img">{t.noImg}</span>}
                                        </td>
                                        <td>{p.name}</td>
                                        <td><span className="category-tag">{p.category}</span></td>
                                        <td>{Number(p.price).toLocaleString()} XAF</td>
                                        <td className={p.stock_quantity < 10 ? 'low-stock' : ''}>{p.stock_quantity}</td>
                                        <td>{p.cbm ? Number(p.cbm).toFixed(4) : '-'}</td>
                                        <td>{p.cbm_price ? `${Number(p.cbm_price).toLocaleString()} XAF` : '-'}</td>
                                        <td className="actions-cell">
                                            <button className="admin-btn small edit" onClick={() => openImageManager(p)} title={t.manageImages}><FaImages /></button>
                                            <button className="admin-btn small edit" onClick={() => openEditForm(p)}><FaEdit /></button>
                                            <button className="admin-btn small danger" onClick={() => handleDeleteProduct(p.id)}><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Product form modal */}
                        {showForm && (
                            <div className="modal-overlay" onClick={() => setShowForm(false)}>
                                <div className="modal" onClick={e => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <h2>{editingProduct ? t.editProduct : t.addNewProduct}</h2>
                                        <button className="modal-close" onClick={() => setShowForm(false)}><FaTimes /></button>
                                    </div>
                                    <form onSubmit={handleFormSubmit} className="product-form">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.productNameLabel}</label>
                                                <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.categoryLabel}</label>
                                                <input type="text" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} required />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t.descriptionLabel}</label>
                                            <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="3" />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.priceXafLabel}</label>
                                                <input type="number" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} required min="0" />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.stockQuantityLabel}</label>
                                                <input type="number" value={formData.stock_quantity} onChange={e => setFormData({...formData, stock_quantity: e.target.value})} min="0" />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.cbmLabel}</label>
                                                <input type="number" step="0.0001" min="0" value={formData.cbm} onChange={e => setFormData({...formData, cbm: e.target.value})} placeholder={t.cbmPlaceholder} />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.cbmPriceLabel}</label>
                                                <input type="number" min="0" value={formData.cbm_price} onChange={e => setFormData({...formData, cbm_price: e.target.value})} placeholder={t.cbmPricePlaceholder} />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t.mainImageLabel}</label>
                                            <input type="file" accept="image/*" disabled={uploadingImage}
                                                onChange={e => handleFileUpload(e.target.files[0], url => setFormData(f => ({ ...f, image_url: url })), setUploadingImage)} />
                                            {uploadingImage && <Loader inline label={t.uploading} />}
                                            <input type="text" value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} placeholder={t.pasteUrlProduct} />
                                            {formData.image_url && <img src={formData.image_url} alt={t.previewAlt} className="form-img-preview" />}
                                        </div>
                                        <button type="submit" className="admin-btn primary full-width">
                                            <FaSave /> {editingProduct ? t.updateProduct : t.createProduct}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* Image manager modal */}
                        {showImageManager && (
                            <div className="modal-overlay" onClick={() => setShowImageManager(null)}>
                                <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <h2>{t.imagesFor(showImageManager.name)}</h2>
                                        <button className="modal-close" onClick={() => setShowImageManager(null)}><FaTimes /></button>
                                    </div>

                                    <div className="img-gallery">
                                        {productImgs.length === 0 && <p className="empty-state">{t.noVariantImages}</p>}
                                        {productImgs.map(img => (
                                            <div key={img.id} className="img-card">
                                                <img src={img.image_url} alt={img.label} />
                                                <div className="img-card-info">
                                                    <span>{img.label || t.noLabel}</span>
                                                    <button className="admin-btn small danger" onClick={() => handleDeleteImage(img.id)}><FaTrash /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <form onSubmit={handleAddImage} className="add-img-form">
                                        <h3>{t.addNewImage}</h3>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.imageLabel}</label>
                                                <input type="file" accept="image/*" disabled={uploadingVariant}
                                                    onChange={e => handleFileUpload(e.target.files[0], url => setNewImage(n => ({ ...n, image_url: url })), setUploadingVariant)} />
                                                {uploadingVariant && <Loader inline label={t.uploading} />}
                                                <input type="text" value={newImage.image_url} onChange={e => setNewImage({...newImage, image_url: e.target.value})}
                                                    placeholder={t.pasteUrlVariant} />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.labelHint}</label>
                                                <input type="text" value={newImage.label} onChange={e => setNewImage({...newImage, label: e.target.value})}
                                                    placeholder={t.variantNamePlaceholder} />
                                            </div>
                                        </div>
                                        {newImage.image_url && <img src={newImage.image_url} alt={t.previewAlt} className="form-img-preview" />}
                                        <button type="submit" className="admin-btn primary"><FaPlus /> {t.addImage}</button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ===== COURSES ===== */}
                {activeTab === 'courses' && (
                    <div className="admin-courses">
                        <div className="admin-header">
                            <h1>{t.coursesCount(courses.length)}</h1>
                            <div className="admin-header-actions">
                                {selectedCourseIds.length > 0 && (
                                    <button className="admin-btn danger" onClick={handleBulkDeleteCourses}>
                                        <FaTrash /> {t.deleteSelected(selectedCourseIds.length)}
                                    </button>
                                )}
                                <button className="admin-btn primary" onClick={openCreateCourse}><FaPlus /> {t.addCourse}</button>
                            </div>
                        </div>
                        {tabLoading.courses && courses.length === 0 && <Loader label={t.loadingCourses} />}
                        <table className="admin-table">
                            <thead><tr>
                                <th className="select-col"><input type="checkbox" checked={courses.length > 0 && selectedCourseIds.length === courses.length} onChange={toggleAllCourses} /></th>
                                <th>{t.colId}</th><th>{t.colImage}</th><th>{t.colTitle}</th><th>{t.colType}</th><th>{t.colPrice}</th><th>{t.colDuration}</th><th>{t.colActions}</th>
                            </tr></thead>
                            <tbody>
                                {courses.map(c => (
                                    <tr key={c.id} className={selectedCourseIds.includes(c.id) ? 'row-selected' : ''}>
                                        <td className="select-col"><input type="checkbox" checked={selectedCourseIds.includes(c.id)} onChange={() => toggleCourseSelection(c.id)} /></td>
                                        <td>#{c.id}</td>
                                        <td>
                                            {c.image_url
                                                ? <img src={c.image_url} alt={c.title} className="table-thumb" />
                                                : <span className="no-img">{t.noImg}</span>}
                                        </td>
                                        <td>{c.title}</td>
                                        <td><span className={`type-badge ${c.course_type}`}>{c.course_type}</span></td>
                                        <td>{Number(c.price).toLocaleString()} XAF</td>
                                        <td>{c.duration || '-'}</td>
                                        <td className="actions-cell">
                                            <button className="admin-btn small edit" onClick={() => openEditCourse(c)}><FaEdit /></button>
                                            <button className="admin-btn small danger" onClick={() => handleDeleteCourse(c.id)}><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {courses.length === 0 && <p className="empty-state">{t.noCoursesYet}</p>}

                        {/* Course form modal */}
                        {showCourseForm && (
                            <div className="modal-overlay" onClick={() => setShowCourseForm(false)}>
                                <div className="modal" onClick={e => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <h2>{editingCourse ? t.editCourse : t.addNewCourse}</h2>
                                        <button className="modal-close" onClick={() => setShowCourseForm(false)}><FaTimes /></button>
                                    </div>
                                    <form onSubmit={handleCourseSubmit} className="product-form">
                                        <div className="form-group">
                                            <label>{t.courseTitleLabel}</label>
                                            <input type="text" value={courseFormData.title} onChange={e => setCourseFormData({...courseFormData, title: e.target.value})} required />
                                        </div>
                                        <div className="form-group">
                                            <label>{t.descriptionLabel}</label>
                                            <textarea value={courseFormData.description} onChange={e => setCourseFormData({...courseFormData, description: e.target.value})} rows="3" />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.courseTypeLabel}</label>
                                                <select value={courseFormData.course_type} onChange={e => setCourseFormData({...courseFormData, course_type: e.target.value})}>
                                                    <option value="online">{t.optOnline}</option>
                                                    <option value="onsite">{t.optOnsite}</option>
                                                    <option value="video">{t.optVideo}</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>{t.priceXafLabelPlain}</label>
                                                <input type="number" value={courseFormData.price} onChange={e => setCourseFormData({...courseFormData, price: e.target.value})} min="0" />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.durationLabel}</label>
                                                <input type="text" value={courseFormData.duration} onChange={e => setCourseFormData({...courseFormData, duration: e.target.value})} placeholder={t.durationPlaceholder} />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.imageUrlLabel}</label>
                                                <input type="text" value={courseFormData.image_url} onChange={e => setCourseFormData({...courseFormData, image_url: e.target.value})} placeholder={t.urlPlaceholder} />
                                            </div>
                                        </div>
                                        {courseFormData.image_url && <img src={courseFormData.image_url} alt={t.previewAlt} className="form-img-preview" />}
                                        <button type="submit" className="admin-btn primary full-width">
                                            <FaSave /> {editingCourse ? t.updateCourse : t.createCourse}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ===== STOCK INVENTORY ===== */}
                {activeTab === 'inventory' && (
                    <InventoryDashboard api={adminInventoryApi} user={user} />
                )}

                {/* ===== PROVIDER GOODS ===== */}
                {activeTab === 'provider-goods' && (
                    <div className="admin-provider-goods">
                        <h1>{t.providerGoodsCount(providerGoods.length)}</h1>
                        <div className="provider-filter-info">
                            <span className="filter-stat pending">{t.pendingStat(providerGoods.filter(g => g.status === 'pending').length)}</span>
                            <span className="filter-stat approved">{t.approvedStat(providerGoods.filter(g => g.status === 'approved').length)}</span>
                            <span className="filter-stat rejected">{t.rejectedStat(providerGoods.filter(g => g.status === 'rejected').length)}</span>
                        </div>
                        {tabLoading['provider-goods'] && providerGoods.length === 0 && <Loader label={t.loadingProviderGoods} />}
                        <table className="admin-table">
                            <thead>
                                <tr><th>{t.colId}</th><th>{t.colImage}</th><th>{t.colName}</th><th>{t.colCategory}</th><th>{t.colPrice}</th><th>{t.colQty}</th><th>{t.colProvider}</th><th>{t.colStatus}</th><th>{t.colDate}</th><th>{t.colActions}</th></tr>
                            </thead>
                            <tbody>
                                {providerGoods.map(g => (
                                    <tr key={g.id}>
                                        <td>#{g.id}</td>
                                        <td>
                                            {g.image_url
                                                ? <img src={g.image_url} alt={g.name} className="table-thumb" />
                                                : <span className="no-img">{t.noImg}</span>}
                                        </td>
                                        <td>
                                            <strong>{g.name}</strong>
                                            {g.description && <p className="good-desc">{g.description}</p>}
                                        </td>
                                        <td><span className="category-tag">{g.category}</span></td>
                                        <td>{Number(g.price).toLocaleString()} XAF</td>
                                        <td>{g.quantity_available}</td>
                                        <td>
                                            <span className="provider-name">{g.provider_name}</span>
                                            <br /><span className="provider-email">{g.provider_email}</span>
                                        </td>
                                        <td><span className={`status-badge ${g.status}`}>{g.status}</span></td>
                                        <td>{new Date(g.created_at).toLocaleDateString()}</td>
                                        <td className="actions-cell provider-actions">
                                            {g.status === 'pending' && (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="admin-note-input"
                                                        placeholder={t.noteOptional}
                                                        value={adminNoteInput}
                                                        onChange={e => setAdminNoteInput(e.target.value)}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <button className="admin-btn small approve" onClick={() => handleApproveGood(g.id)} title={t.approveAddToStore}><FaCheck /></button>
                                                    <button className="admin-btn small danger" onClick={() => handleRejectGood(g.id)} title={t.reject}><FaBan /></button>
                                                </>
                                            )}
                                            {g.status !== 'pending' && (
                                                <span className="admin-note-display">{g.admin_note || '-'}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {providerGoods.length === 0 && <p className="empty-state">{t.noProviderGoods}</p>}
                    </div>
                )}

                {/* ===== REVIEWS ===== */}
                {activeTab === 'reviews' && (
                    <div className="admin-reviews">
                        <h1>{t.customerReviewsCount(reviews.length)}</h1>
                        <div className="provider-filter-info">
                            <span className="filter-stat approved">{t.visibleStat(reviews.filter(r => !r.is_hidden).length)}</span>
                            <span className="filter-stat rejected">{t.hiddenStat(reviews.filter(r => r.is_hidden).length)}</span>
                        </div>
                        {tabLoading.reviews && reviews.length === 0 && <Loader label={t.loadingReviews} />}
                        <table className="admin-table">
                            <thead>
                                <tr><th>{t.colId}</th><th>{t.colProduct}</th><th>{t.colCustomer}</th><th>{t.colRating}</th><th>{t.colComment}</th><th>{t.colDate}</th><th>{t.colStatus}</th><th>{t.colActions}</th></tr>
                            </thead>
                            <tbody>
                                {reviews.map(r => (
                                    <tr key={r.id} className={r.is_hidden ? 'hidden-row' : ''}>
                                        <td>#{r.id}</td>
                                        <td>{r.product_name}</td>
                                        <td>
                                            <span className="provider-name">{r.user_name}</span>
                                            <br /><span className="provider-email">{r.user_email}</span>
                                        </td>
                                        <td><span className="review-rating-cell">{renderStars(r.rating)}</span></td>
                                        <td className="review-comment-cell">{r.comment || <em>{t.noComment}</em>}</td>
                                        <td>{new Date(r.created_at).toLocaleDateString()}</td>
                                        <td>
                                            <span className={`status-badge ${r.is_hidden ? 'cancelled' : 'delivered'}`}>
                                                {r.is_hidden ? t.hidden : t.visible}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                className="admin-btn small edit"
                                                onClick={() => handleToggleReviewVisibility(r)}
                                                title={r.is_hidden ? t.showReview : t.hideReview}
                                            >
                                                {r.is_hidden ? <FaEye /> : <FaEyeSlash />}
                                            </button>
                                            <button
                                                className="admin-btn small danger"
                                                onClick={() => handleDeleteReview(r.id)}
                                                title={t.deleteReview}
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {reviews.length === 0 && <p className="empty-state">{t.noReviews}</p>}
                    </div>
                )}

                {/* ===== ORDERS ===== */}
                {activeTab === 'orders' && (
                    <div className="admin-orders">
                        <h1>{t.ordersCount(orders.length)}</h1>
                        {tabLoading.orders && orders.length === 0 && <Loader label={t.loadingOrders} />}
                        <table className="admin-table">
                            <thead><tr><th>{t.colId}</th><th>{t.colCustomer}</th><th>{t.colEmail}</th><th>{t.colAmount}</th><th>{t.colStatus}</th><th>{t.colDate}</th></tr></thead>
                            <tbody>
                                {orders.map(order => (
                                    <tr key={order.id}>
                                        <td>#{order.id}</td><td>{order.customer_name}</td><td>{order.customer_email}</td>
                                        <td>{Number(order.total_amount).toLocaleString()} XAF</td>
                                        <td>
                                            <select className={`status-select ${order.status}`} value={order.status}
                                                onChange={e => handleStatusChange(order.id, e.target.value)}>
                                                <option value="pending">{t.statusPending}</option>
                                                <option value="processing">{t.statusProcessing}</option>
                                                <option value="shipped">{t.statusShipped}</option>
                                                <option value="delivered">{t.statusDelivered}</option>
                                                <option value="cancelled">{t.statusCancelled}</option>
                                            </select>
                                        </td>
                                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {orders.length === 0 && <p className="empty-state">{t.noOrders}</p>}
                    </div>
                )}

                {/* ===== USERS ===== */}
                {activeTab === 'users' && (
                    <div className="admin-users">
                        <div className="admin-header">
                            <h1>{t.usersCount(users.length)}</h1>
                            <button className="admin-btn primary" onClick={() => setShowUserForm(true)}><FaPlus /> {t.addUser}</button>
                        </div>
                        {tabLoading.users && users.length === 0 && <Loader label={t.loadingUsers} />}
                        <table className="admin-table">
                            <thead><tr><th>{t.colId}</th><th>{t.colName}</th><th>{t.colEmail}</th><th>{t.colRole}</th><th>{t.colPermissions}</th><th>{t.colJoined}</th></tr></thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td>#{u.id}</td><td>{u.name}</td><td>{u.email}</td>
                                        <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                                        <td>
                                            {u.role === 'admin' ? (
                                                <span className="perm-badge all">{t.fullAccess}</span>
                                            ) : (Array.isArray(u.permissions) && u.permissions.length > 0) ? (
                                                <div className="perm-badges">
                                                    {u.permissions.map(p => <span key={p} className="perm-badge">{p}</span>)}
                                                </div>
                                            ) : (
                                                <span className="perm-badge none">—</span>
                                            )}
                                        </td>
                                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {showUserForm && (
                            <div className="modal-overlay" onClick={() => setShowUserForm(false)}>
                                <div className="modal" onClick={e => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <h2>{t.createNewUser}</h2>
                                        <button className="modal-close" onClick={() => setShowUserForm(false)}><FaTimes /></button>
                                    </div>
                                    <form onSubmit={handleCreateUser} className="product-form">
                                        <div className="form-group">
                                            <label>{t.fullNameLabel}</label>
                                            <input type="text" value={userFormData.name} onChange={e => setUserFormData({...userFormData, name: e.target.value})} required />
                                        </div>
                                        <div className="form-group">
                                            <label>{t.emailLabel}</label>
                                            <input type="email" value={userFormData.email} onChange={e => setUserFormData({...userFormData, email: e.target.value})} required />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.passwordLabel}</label>
                                                <input type="password" value={userFormData.password} onChange={e => setUserFormData({...userFormData, password: e.target.value})} required minLength={6} placeholder={t.passwordPlaceholder} />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.roleLabel}</label>
                                                <select value={userFormData.role} onChange={e => setUserFormData({...userFormData, role: e.target.value})}>
                                                    <option value="user">{t.roleUser}</option>
                                                    <option value="admin">{t.roleAdmin}</option>
                                                    <option value="provider">{t.roleProvider}</option>
                                                </select>
                                            </div>
                                        </div>

                                        {userFormData.role !== 'admin' && (
                                            <div className="form-group">
                                                <label>{t.adminPanelAccess}</label>
                                                <p className="perm-help">{t.permHelp}</p>
                                                <div className="perm-grid">
                                                    {PERMISSION_OPTIONS.map(opt => (
                                                        <label key={opt.key} className={`perm-chip ${userFormData.permissions.includes(opt.key) ? 'checked' : ''}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={userFormData.permissions.includes(opt.key)}
                                                                onChange={() => togglePerm(opt.key)}
                                                            />
                                                            <span>{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <p className="email-note">
                                            <FaEnvelope /> {t.emailNote}
                                        </p>

                                        <button type="submit" className="admin-btn primary full-width">
                                            <FaSave /> {t.createUserSendEmail}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ===== MESSAGES ===== */}
                {activeTab === 'messages' && (
                    <div className="admin-messages">
                        <h1>{t.contactMessagesCount(messages.length)}</h1>
                        {tabLoading.messages && messages.length === 0 && <Loader label={t.loadingMessages} />}
                        <div className="messages-list">
                            {messages.map(msg => (
                                <div key={msg.id} className="message-card">
                                    <div className="message-header">
                                        <div><strong>{msg.full_name}</strong><span className="message-email">{msg.email}</span></div>
                                        <div className="message-actions">
                                            <span className="message-date">{new Date(msg.created_at).toLocaleDateString()}</span>
                                            <button className="admin-btn small danger" onClick={() => handleDeleteMessage(msg.id)}><FaTrash /></button>
                                        </div>
                                    </div>
                                    {msg.subject && <p className="message-subject">{msg.subject}</p>}
                                    <p className="message-body">{msg.message}</p>
                                    {msg.phone && <p className="message-phone">{t.phoneLabel(msg.phone)}</p>}
                                </div>
                            ))}
                            {messages.length === 0 && <p className="empty-state">{t.noMessages}</p>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Admin;
